<template>
  <v-row v-if="hasFilter" class="py-2" no-gutters align="end">
    <!-- text-field searching by text -->
    <v-col
      v-if="hasSearch"
      cols="12"
      :md="getToSearchWidthClass"
      class="pb-sm-2 py-md-0 pl-md-0"
      :class="{
        'pr-md-3':
          hasMultiSelect ||
          hasDateRangePicker ||
          hasCheckboxSelection ||
          hasSort
      }"
    >
      <v-text-field
        v-model="toSearch"
        :label="$t(`${pageType}.searchLabel`)"
        :title="$t(`${pageType}.searchLabel`)"
        ref="toSearch"
        append-icon="fas fa-search fa-sm"
        clear-icon="fas fa-times fa-sm"
        @click:append="search"
        @keyup.enter.native="search"
        @click:clear="clearSearch"
        clearable
        hide-details
      ></v-text-field>
    </v-col>

    <!-- autocomplete filtering by multiple values -->
    <v-col
      v-if="hasMultiSelect"
      sm="12"
      :md="getMultiSelectWidthClass"
      class="py-sm-2 py-md-0 pl-md-0"
      :class="{
        'pr-md-3': hasDateRangePicker || hasCheckboxSelection || hasSort
      }"
    >
      <v-autocomplete
        v-model="multiSelectActiveList"
        :items="multiSelectFullList"
        :label="$t(`${pageType}.multiSelectLabel`)"
        :title="$t(`${pageType}.multiSelectLabel`)"
        :no-data-text="$t(`${pageType}.multiSelectNoDataMsg`)"
        :filter="autocompleteSearch"
        ref="multiSelect"
        class="input-multi-select"
        multiple
        chips
        clearable
        clear-icon="fas fa-times fa-sm"
        hide-details
        auto-select-first
        :menu-props="{ closeOnContentClick: true, maxWidth: 'min-content' }"
      >
        <template v-slot:selection="data">
          <v-tooltip class="service-item" color="primary" bottom>
            <template v-slot:activator="{ on }">
              <v-chip
                v-on="on"
                :input-value="data.selected"
                class="service-chip text-uppercase white--text my-1 mr-1"
                small
                color="primary"
                close
                @click:close="removeMultiSelectItem(data.item)"
                ><span class="font-weight-bold text-truncate">
                  <span v-if="translateMultiSelectItem">{{
                    $t(`baseFilters.${pageType}.${data.item}Checkbox`)
                  }}</span>
                  <span v-else>{{ data.item }}</span>
                </span></v-chip
              >
            </template>
            <span class="font-weight-bold text-uppercase caption">
              <span v-if="translateMultiSelectItem">{{
                $t(`baseFilters.${pageType}.${data.item}Checkbox`)
              }}</span>
              <span v-else>{{ data.item }}</span>
            </span>
          </v-tooltip>
        </template>
        <template v-slot:item="data">
          <v-list-item-content class="text-uppercase">
            <span v-if="translateMultiSelectItem">{{
              $t(`baseFilters.${pageType}.${data.item}Checkbox`)
            }}</span>
            <span v-else>{{ data.item }}</span>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-col>

    <!-- date-picker filtering by date -->
    <v-col
      v-if="hasDateRangePicker"
      cols="6"
      :md="dateRangeWidthClass"
      class="date-range-picker-element py-sm-2 py-md-0 pl-md-0"
      :class="{
        'pr-3': hasSort
      }"
    >
      <v-row no-gutters="" align="end" style="height: 100%">
        <v-col>
          <base-rangedate-picker
            style="height: 100%"
            :storeType="storeType"
            @retrieveitems="retrieveItems"
          />
        </v-col>
      </v-row>
    </v-col>
    <!-- checkbox filtering by selection (used only in machinery page) -->
    <v-col
      v-if="hasCheckboxSelection"
      sm="12"
      :md="getCheckboxSelectionWidthClass"
      class="pr-md-3 py-sm-2 py-md-0 pl-md-0"
    >
      <v-row no-gutters justify-space-between align="end">
        <v-col v-for="(checkboxItem, index) in checkboxFullList" :key="index">
          <v-checkbox
            v-model="checkboxSelectedList"
            :value="checkboxItem.value"
            :label="$t(`baseFilters.${pageType}.${checkboxItem.label}`)"
            on-icon="far fa-dot-circle"
            off-icon="far fa-circle"
            hide-details
          ></v-checkbox>
        </v-col>
      </v-row>
    </v-col>

    <!-- apply sort -->
    <v-col
      v-if="hasSort"
      class="py-sm-2 py-md-0 pl-md-0"
      sm="12"
      :md="getSortWidthClass"
    >
      <base-single-select
        :label="getSortElementLabel"
        :title="getSortElementLabel"
        :item="order"
        :list="orderList"
        @selected="updateOrder"
      />
    </v-col>
    <!-- Results -->
    <v-row
      class="no-gutters align-center justify-space-between mt-8"
      v-if="!noItems"
    >
      <v-col class="pt-sm-2 py-md-0 pl-md-0" cols="8">
        <h2 v-if="filterCriteriaResults" class="subtitle-1">
          <span class="font-weight-bold black--text"
            >{{ $t("baseFilters.results-pt1") }}&nbsp;</span
          >
          <span class="font-weight-bold secondary--text">{{ totalItems }}</span>
          <span class="font-weight-bold black--text"
            >&nbsp;{{ $t("baseFilters.results-pt2") }}&nbsp;</span
          >
          <span class="font-weight-bold secondary--text">{{
            filterCriteriaResults
          }}</span>
          <span class="font-weight-bold black--text" v-if="activeSortValue"
            >&nbsp;{{ $t("baseFilters.results-pt5") }}&nbsp;</span
          >
          <span class="font-weight-bold secondary--text" v-if="activeSortValue">
            {{ activeSortValue }}</span
          >
        </h2>
        <h2 v-else class="subtitle-1">
          <span class="font-weight-bold black--text"
            >{{ $t("baseFilters.results-pt3") }}:&nbsp;</span
          >
          <span class="font-weight-bold secondary--text">{{ totalItems }}</span>
          <span class="font-weight-bold black--text"
            >&nbsp;{{ $t("baseFilters.results-pt4") }}&nbsp;</span
          >
          <span class="font-weight-bold black--text" v-if="activeSortValue"
            >&nbsp;{{ $t("baseFilters.results-pt5") }}&nbsp;</span
          >
          <span class="font-weight-bold secondary--text" v-if="activeSortValue">
            {{ activeSortValue }}</span
          >
        </h2>
      </v-col>
      <v-col
        cols="4"
        class="d-flex justify-end align-center"
        v-if="isBookingAppointmets"
      >
        <v-btn
          color="primary"
          small
          rounded
          @click="addAppointmentsIntoBill()"
          outlined
          :disabled="isAddIntoBillsDisabled"
          >{{ $t("appointments.booking.addIntoBills") }}</v-btn
        ></v-col
      >
    </v-row>

    <!-- No results: we are using the component BaseTableNoDataNoLocalized or BaseTableNoData to print the messages -->

    <!-- No results without table -->
    <v-col
      v-if="noItems && watchMessageResultNoTable"
      class="mt-8 pt-sm-2 py-md-0 pl-md-0"
      cols="12"
    >
      <h2 v-if="seeMessageResultNoTable" class="subtitle-1">
        <v-icon left color="primary">fas fa-times-circle</v-icon>
        <span class="font-weight-bold black--text"
          >{{ $t("baseFilters.results-pt1") }}&nbsp;</span
        >
        <span class="font-weight-bold secondary--text">{{ totalItems }}</span>
        <span class="font-weight-bold black--text"
          >&nbsp;{{ $t("baseFilters.results-pt2") }}&nbsp;</span
        >
        <span class="font-weight-bold secondary--text">{{
          filterCriteriaResults
        }}</span>
        <span class="font-weight-bold black--text" v-if="activeSortValue"
          >&nbsp;{{ $t("baseFilters.results-pt5") }}&nbsp;</span
        >
        <span class="font-weight-bold secondary--text" v-if="activeSortValue">
          {{ activeSortValue }}</span
        >
      </h2>
      <h2 v-else class="subtitle-1">
        <span class="font-weight-bold black--text"
          >{{ $t("baseFilters.results-pt3") }}:&nbsp;</span
        >
        <span class="font-weight-bold secondary--text">{{ totalItems }}</span>
        <span class="font-weight-bold black--text"
          >&nbsp;{{ $t("baseFilters.results-pt4") }}&nbsp;</span
        >
        <span class="font-weight-bold black--text" v-if="activeSortValue"
          >&nbsp;{{ $t("baseFilters.results-pt5") }}&nbsp;</span
        >
        <span class="font-weight-bold secondary--text" v-if="activeSortValue">
          {{ activeSortValue }}</span
        >
      </h2>
    </v-col>
  </v-row>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import findIndex from "lodash/findIndex";
import BaseSingleSelect from "./BaseSingleSelect";
import store from "../store";

export default {
  props: {
    pageType: {
      type: String,
      required: false
    },
    storeType: {
      type: String,
      required: false
    },
    translateMultiSelectItem: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  components: {
    BaseSingleSelect
  },
  data() {
    return {
      toSearch: ""
    };
  },
  created() {
    this.initialize();
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.toSearch.focus();
    });
  },
  computed: {
    isSanimpresaOffice() {
      return this.$store.getters["hfManagement/isSanimpresaOffice"];
    },
    getSortElementLabel() {
      if (this.pageType === "bills") {
        return "";
      }
      let path = `baseFilters.${this.storeType}.sortLabel`;
      let labelBasedOnPage = this.$t(path);
      return labelBasedOnPage === path
        ? this.$t("sortDefault")
        : labelBasedOnPage;
    },
    hasFilter() {
      return (
        this.hasSearch ||
        this.hasMultiSelect ||
        this.hasSort ||
        this.hasDateRangePicker ||
        this.hasCheckboxSelection
      );
    },
    hasSearch() {
      return (
        this.pageType === "operators" ||
        this.pageType === "bills" ||
        this.pageType === "appointments" ||
        this.pageType === "processed-requests" ||
        this.pageType === "newsTutorial" ||
        this.pageType === "billAppointments" ||
        this.storeType === "doctors" ||
        this.storeType === "machinery" ||
        this.storeType === "members" ||
        this.storeType === "memberAppointments"
      );
    },
    hasMultiSelect() {
      return (
        this.pageType === "operators" ||
        this.pageType === "bills" ||
        this.pageType === "appointments" ||
        this.pageType === "newsTutorial" ||
        this.storeType === "doctors" ||
        this.storeType === "machinery" ||
        this.storeType === "memberAppointments"
      );
    },
    hasSort() {
      return (
        this.pageType === "operators" ||
        this.pageType === "billAppointments" ||
        this.storeType === "doctors" ||
        this.storeType === "machinery" ||
        this.storeType === "newsTutorial"
      );
    },
    hasDateRangePicker() {
      return (
        this.pageType === "bills" ||
        this.pageType === "appointments" ||
        this.pageType === "billAppointments" ||
        this.pageType === "newsTutorial" ||
        this.pageType === "members"
      );
    },
    hasCheckboxSelection() {
      return this.pageType === "hfManagement.machineryPerformance";
    },
    getSortWidthClass() {
      switch (this.pageType) {
        case "operators":
        case "hfManagement.officeStaff":
          return "2";
        default:
          return "1";
      }
    },
    getToSearchWidthClass() {
      switch (this.pageType) {
        case "operators":
        case "hfManagement.officeStaff":
          return "4";
        case "members":
          return this.storeType === "memberAppointments" ? "4" : "6";
        case "billAppointments":
          return "8";
        default:
          return "3";
      }
    },
    getMultiSelectWidthClass() {
      switch (this.pageType) {
        case "hfManagement.machineryPerformance":
          return "4";
        case "appointments":
        case "bills":
          return "6";
        case "newsTutorial":
          return "5";
        case "operators":
        case "hfManagement.officeStaff":
          return "6";
        default:
          return "5";
      }
    },
    getCheckboxSelectionWidthClass() {
      if (this.pageType === "hfManagement.machineryPerformance") {
        return "4";
      }
      return "12";
    },
    dateRangeWidthClass() {
      return "3";
    },
    multiSelectActiveList: {
      get: function () {
        return this.$store.getters[`${this.storeType}/multiSelectActiveList`];
      },
      set: function (value) {
        this.$refs.multiSelect.blur();
        this.$store.commit(
          `${this.storeType}/SET_MULTI_SELECT_ACTIVE_LIST`,
          value
        );
        this.retrieveItems();
      }
    },
    multiSelectFullList() {
      let items = this.$store.getters[`${this.storeType}/multiSelectFullList`];
      if (this.storeType === "bills") {
        if (this.isSanimpresaOffice) {
          let copy = [...items];
          copy.splice(copy.indexOf("ACCEPTED"), 1);
          copy.splice(copy.indexOf("CROTOBECONFIRMED"), 1);
          copy.splice(copy.indexOf("UNPAID"), 1);
          copy.splice(copy.indexOf("CREDITNOTE"), 1);
          copy.splice(copy.indexOf("CREDITNOTEPAID"), 1);
          return copy;
        }
      }
      return items;
    },
    order() {
      return this.$store.getters[`${this.storeType}/order`];
    },
    orderList() {
      return this.$store.getters[`${this.storeType}/orderList`];
    },
    totalItems() {
      return this.$store.getters[`${this.storeType}/totalItems`];
    },
    noItems() {
      return this.totalItems === 0;
    },
    checkboxFullList() {
      return this.$store.getters[`${this.storeType}/checkboxFullList`];
    },
    checkboxSelectedList: {
      get() {
        return this.$store.getters[`${this.storeType}/checkboxSelectedList`];
      },
      set(selectedList) {
        this.$store.commit(
          `${this.storeType}/SET_CHECKBOX_SELECTED_LIST`,
          selectedList
        );
        this.retrieveItems();
      }
    },
    filterCriteriaResults() {
      return this.$store.getters[`${this.storeType}/filterCriteriaResults`];
    },
    activeSortValue() {
      return this.$store.getters[`${this.storeType}/activeSortValue`];
    },
    seeMessageResult() {
      return (
        this.filterCriteriaResults && this.filterCriteriaResults.length >= 1
      );
    },
    watchMessageResult() {
      return this.storeType === "appointments";
    },
    seeMessageResultNoTable() {
      return (
        this.filterCriteriaResults && this.filterCriteriaResults.length >= 1
      );
    },
    watchMessageResultNoTable() {
      let validStores = [
        "machinery",
        "doctors",
        "operators",
        "bills",
        "memberAppointments",
        "billAppointments",
        "newsTutorial"
      ];
      return validStores.includes(this.storeType);
    },
    isBookingAppointmets() {
      if (this.storeType === "appointments") {
        return this.$store.getters["appointments/type"] == "booking"
          ? true
          : false;
      }
      return false;
    },
    isAddIntoBillsDisabled() {
      const ids = this.$store.getters["appointments/getSelectedAppointmentIds"];
      return ids.length === 0;
    }
  },
  methods: {
    initialize() {
      this.toSearch = cloneDeep(
        this.$store.getters[`${this.storeType}/toSearch`]
      );
      //Clear the appointments ids list
      if (this.isBookingAppointmets) {
        this.$store.commit("appointments/clearSelectedAppointmentsIds");
      }
    },
    search() {
      this.$refs.toSearch.blur();
      this.retrieveItems(this.toSearch);
    },
    clearSearch() {
      this.retrieveItems("");
    },
    removeMultiSelectItem(item) {
      this.$refs.multiSelect.blur();
      let customRemovalStoreTypes = ["bills", "appointments"];
      if (customRemovalStoreTypes.includes(this.storeType)) {
        this.$store.commit(`${this.storeType}/REMOVE_MULTI_SELECT_ITEM`, item);
      } else {
        let multiSelectList = [...this.multiSelectActiveList];
        let index = findIndex(multiSelectList, listItem => listItem === item);
        if (index !== -1) {
          multiSelectList.splice(index, 1);
        }
        this.$store.commit(
          `${this.storeType}/SET_MULTI_SELECT_ACTIVE_LIST`,
          multiSelectList
        );
      }
      this.retrieveItems();
    },
    autocompleteSearch(item, queryText) {
      const translatedItem = this.translateMultiSelectItem
        ? this.$t(`baseFilters.${this.pageType}.${item}Checkbox`).toLowerCase()
        : item.toLowerCase();
      const searchText = queryText.toLowerCase();
      return translatedItem.indexOf(searchText) > -1;
    },
    updateOrder(value) {
      this.$store.commit(`${this.storeType}/SET_ORDER`, value);
      this.retrieveItems();
    },
    retrieveItems(query) {
      if (query || query === "") {
        this.$store.commit(`${this.storeType}/SET_TO_SEARCH`, query);
      } else {
        this.$store.commit(`${this.storeType}/SET_TO_SEARCH`, this.toSearch);
      }
      this.$emit("retrieveitems");
    },
    async addAppointmentsIntoBill() {
      //reate a new bill
      await this.$store.dispatch("bills/createBill");
      const billId = this.$store.getters["bills/activeBillId"];
      if (billId) {
        //add appointments into bills
        await this.$store.dispatch(
          "appointments/addSelectedAppointmentToBills"
        );
        //redirect to billsDetails
        this.$store.commit("bills/SET_ACTIVE_BILL_ID", billId);
        await this.$router.push({
          name: "_billDetails",
          params: { id: billId }
        });
      }
    }
  }
};
</script>

<style scoped>
.input-multi-select >>> .service-chip .v-chip__content {
  max-width: 380px;
}
.date-range-picker-element {
  height: 60px !important;
}
.input-multi-select >>> .v-input__append-inner {
  align-self: flex-end;
  padding-bottom: 4px;
}
</style>
